import { DialogTitle } from "@headlessui/react";
import { useQueryClient } from "@tanstack/react-query";

import { JSX, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Timeout } from "react-number-format/types/types";
import { useSearchParams } from "react-router-dom";

import Button from "@/components/buttons/Button.tsx";
import { IconInfoCircle, IconPaymentResult } from "@/components/icons";

import { USER_KEYS, useGetOrder } from "@/hooks/api/user.hooks";
import { useLang } from "@/hooks/useLang";
import useModalRouter from "@/hooks/useModalRouter";

import Spinner from "../helpers/Spinner/Spinner";
import { MODAL_ROUTES } from "./router/ModalRoutes";

const OrderPerformSuccessModal = (): JSX.Element => {
  const { t } = useTranslation();

  const intervalTimer = useRef<Timeout>();

  const [params] = useSearchParams();

  const orderId = useMemo(() => params.get("order_id"), []);

  const lang = useLang();

  const { closeModal, openModal } = useModalRouter();

  const { data: order } = useGetOrder(+(orderId ?? ""));

  const queryClient = useQueryClient();

  useEffect(() => {
    if (order?.status === "performed") {
      queryClient.invalidateQueries({ queryKey: USER_KEYS.userDetail(lang) });
      queryClient.invalidateQueries({ queryKey: USER_KEYS.orders(lang) });

      clearInterval(intervalTimer.current);

      return;
    }

    if (order?.status === "canceled") {
      openModal(MODAL_ROUTES.ORDER_PERFORM_ERROR, {}, true);

      clearInterval(intervalTimer.current);

      return;
    }

    intervalTimer.current = setInterval(() => {
      if (orderId) queryClient.invalidateQueries({ queryKey: USER_KEYS.order(lang, +orderId) });
    }, 2000);

    return () => {
      clearInterval(intervalTimer.current);
    };
  }, [order?.status]);

  useEffect(() => {
    const tempOrder = order;
    return () => {
      if (tempOrder?.status == "performed" && tempOrder.order_type == "subscription" && tempOrder?.item_id) {
        openModal(MODAL_ROUTES.AUTO_PAYMENT, { subs_id: tempOrder?.item_id });
      }
    };
  }, [order]);

  if (!order || order.status != "performed") return <Spinner />;

  const date = new Date(order.updated_at);

  const formattedDate = date.toLocaleDateString("ru", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
  });

  return (
    <div className={"relative flex flex-col items-center gap-4"}>
      <IconPaymentResult
        name={"success"}
        className={"absolute -top-[4rem] left-1/2 size-[5rem] -translate-x-1/2 transform"}
      />
      <DialogTitle as={"h2"} className={"text-center text-[2rem]/normal font-semibold"}>
        {t("General.success")}
      </DialogTitle>
      <div className={"w-full space-y-2"}>
        <div className={"space-y-1.5"}>
          <h3 className={"text-grey-1"}>{t("Payment.amount")}</h3>
          <p className={"text-xl max-lg:text-base"}>
            {t("General.som", {
              value: ((order.amount == 0 ? order.balance_sum : order.amount) / 100).thousandSeparator(),
            })}
          </p>
        </div>
        {order.cashback_sum > 0 && (
          <>
            <hr className={"h-px w-full border-outline"} />
            <div className={"flex items-end justify-between"}>
              <div className={"space-y-1.5"}>
                <h3 className={"text-grey-1"}>{t("Payment.cashback")}</h3>
                <p className={"text-xl text-[#66C61C] max-lg:text-base"}>
                  {t("General.som", { value: (order.cashback_sum / 100).thousandSeparator() })}
                </p>
              </div>
              <div className={"group relative"}>
                <IconInfoCircle className={"size-6 stroke-grey-1 transition-colors hover:stroke-on-primary"} />
                <div
                  className={
                    "pointer-events-none invisible absolute -bottom-2 -right-2 translate-y-full transform opacity-0 transition-all group-hover:pointer-events-auto group-hover:visible group-hover:opacity-100"
                  }
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 17 8"
                    className={"absolute -top-2 right-3 h-2 w-4 fill-black-1"}
                  >
                    <path d="M0.5 8L16.5 8L9.91421 1.41421C9.13317 0.633164 7.86684 0.633164 7.08579 1.41421L0.5 8Z" />
                  </svg>
                  <div className={"w-[11.25rem] rounded-lg bg-black-1 p-3 text-center"}>
                    {t("Payment.cashback-info")}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        <hr className={"h-px w-full border-outline"} />
        <div className={"space-y-1.5"}>
          <h3 className={"text-grey-1"}>{t("Payment.date-of-payment")}</h3>
          <p className={"text-xl max-lg:text-base"}>{formattedDate}</p>
        </div>
        {order.payment_service && (
          <>
            <hr className={"h-px w-full border-outline"} />
            <div className={"space-y-1.5"}>
              <h3 className={"text-grey-1"}>{t("Payment.method")}</h3>
              <p className={"text-xl max-lg:text-base"}>{order.payment_service?.toUpperCase()}</p>
            </div>
          </>
        )}
        <hr className={"h-px w-full border-outline"} />
        <div className={"space-y-1.5"}>
          <h3 className={"text-grey-1"}>{t("Payment.category")}</h3>
          <p className={"text-xl max-lg:text-base"}>{t(`Order.status.${order.order_type}`)}</p>
        </div>
      </div>
      <Button onClick={closeModal} className={"w-fit min-w-80"}>
        {t("General.ready")}
      </Button>
    </div>
  );
};

export default OrderPerformSuccessModal;
